import { Info } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';
import styles from './AcknowledgementNotice.module.scss';

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Paper,
  Typography
} from '@mui/material';

const sanitizeHTML = html => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.innerHTML;
};

export default function AcknowledgementNotice({
  title = '',
  message = '',
  checkboxLabel = 'I acknowledge',
  checked = false,
  onChange,
  isError = false,
  errorMessage = 'You must acknowledge this notice to continue',
  variant = 'info',
  blackoutStates = [],
  disabled = false,
  addBottomMargin = true
}) {
  const handleChange = event => {
    if (disabled) return; // prevent changes if disabled
    const newCheckedState = event.target.checked;
    onChange && onChange(newCheckedState);
  };

  return (
    <FormControl
      error={isError}
      sx={{ width: '100%', marginBottom: addBottomMargin ? '1rem' : '0' }}
    >
      {title && (
        <Typography variant='body1' className={styles.title}>
          {title}
        </Typography>
      )}
      <Paper
        className={`${styles.noticeContainer} ${isError ? styles.error : ''} ${
          styles[variant]
        }`}
        elevation={1}
      >
        <Box className={styles.noticeHeader}>
          <Info size={24} className={styles.noticeIcon} />
          {message && (
            <div>
              <div
                className={styles.noticeMessage}
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }}
              />
              {blackoutStates?.length > 0 && (
                <div className={styles.subtitle}>
                  <Typography variant='body1' className={styles.statesSubtitle}>
                    Blackout Hold is active for the following requested states.
                    Other states not listed will be processed normally.
                  </Typography>
                  <ul className={styles.stateList}>
                    {blackoutStates.map(state => (
                      <li key={state} className={styles.state}>
                        {state}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={handleChange}
              color='primary'
              className={styles.checkbox}
            />
          }
          label={checkboxLabel}
          className={styles.checkboxLabel}
        />
      </Paper>
      {isError && (
        <FormHelperText error={isError}>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  );
}

AcknowledgementNotice.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  checkboxLabel: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  isError: PropTypes.bool,
  errorMessage: PropTypes.string,
  variant: PropTypes.oneOf(['info', 'warning', 'error', 'success']),
  blackoutStates: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  addBottomMargin: PropTypes.bool
};
